<template>
  <NuxtLayout name="app">
    <Card class="md:w-[38rem]">
      <template #title>{{ t('title') }}</template>
      <template #subTitle>{{ t('subTitle') }}</template>
      <template #default>
        <form @submit.stop.prevent="handleContinueClick">
          <div class="space-y-8">
            <PulseFeedback v-if="requestError && !loading" variant="error">{{ requestError }}</PulseFeedback>
            <PulseFeedback v-if="requestWarning && !loading" variant="warning">{{ requestWarning }}</PulseFeedback>
            <div class="space-y-6">
              <PulseValidationGroup v-slot="{ error }" field="orderNumber">
                <PulseInput
                  id="order-number"
                  v-model="orderNumber"
                  v-hubble="'order-number-input'"
                  :error="error"
                  :label="t('formLabels.orderNumber')"
                  required
                  name="orderNumber"
                  autofocus
                  :disabled="loading"
                />
              </PulseValidationGroup>
              <PulseValidationGroup v-slot="{ error }" field="emailAddress">
                <PulseInput
                  id="email-address"
                  v-model="emailAddress"
                  v-hubble="'email-address-input'"
                  :error="error"
                  :label="t('formLabels.emailAddress')"
                  required
                  name="emailAddress"
                  :disabled="loading"
                />
              </PulseValidationGroup>
            </div>
            <PulseButton v-hubble="'continue-button'" type="submit" class="w-full" size="large" :loading="loading">{{
              t('buttons.continue')
            }}</PulseButton>
          </div>
          <div
            v-if="inStoreEnabled"
            class="text-sm cursor-pointer mt-4 text-center font-semibold text-primary-700"
            @click="handleInStorePurchaseClick"
          >
            <span>{{ t('subtext') }}</span>
          </div>
        </form>
        <OtaModal :open="showOtaModal" :title="otaHeader" :eligibility="otaBodyText" @close="handleOtaClose" />
      </template>
    </Card>
  </NuxtLayout>
</template>

<script setup>
import { useClaimStore } from '@pulse/composables/useClaimStore';
import { useInStore } from '@pulse/composables/useInStore';
import { useOrdersStore } from '@pulse/composables/useOrdersStore';
import { ClaimCategory } from '@pulse/constants/claims';
import { StartPageActions } from '@pulse/constants/orders';
import { required } from '@pulse/utils/validation';
import { isEmpty } from 'lodash';

import useReturnsRouting from '~/composables/useReturnsRouting';
import { useMerchantStore, useWhiteLabelStore } from '~/store';

const { t } = useI18n({
  useScope: 'local',
});

const route = useRoute();

const runtimeConfig = useRuntimeConfig();
const orderStore = useOrdersStore(runtimeConfig)();
const claimStore = useClaimStore(runtimeConfig)();

const merchantStore = useMerchantStore();
const inStore = useInStore(runtimeConfig)();
const whitelabel = useWhiteLabelStore();

const requestError = ref(null);
const requestWarning = ref(null);
const inStoreEnabled = ref(false);

const emailAddress = ref(route.query.email || '');
const slug = ref(route.query.slug || '');

const loading = ref(false);
const orderNumber = ref(route.query.order || '');
const action = ref(route.query?.action ?? StartPageActions.VIEW);
const showOtaModal = ref(false);
const returnRouter = useReturnsRouting();
const claimRouter = useClaimRouting();

function cleanOrderNumber(orderNumber) {
  return orderNumber.replace(/#/g, '');
}

const $v = useValidation(
  {
    emailAddress: {
      required,
    },
    orderNumber: {
      required,
    },
  },
  {
    emailAddress: computed(() => emailAddress.value),
    orderNumber: computed(() => orderNumber.value),
  },
);

watch(
  [() => route.query.email, () => route.query.order],
  ([newEmail, newOrder]) => {
    if (!newEmail && !newOrder) return;

    emailAddress.value = newEmail;
    orderNumber.value = newOrder;
    handleContinueClick();
  },
  { immediate: true },
);

const otaHeader = computed(() => orderStore.currentOrder?.settings?.otaHeader);
const otaBodyText = computed(() => orderStore.currentOrder?.settings?.otaBodyText);

function handleOtaClose() {
  showOtaModal.value = false;

  claimRouter.validate();
}

async function handleFindOrderSuccess() {
  const currentOrder = toRaw(orderStore.currentOrder);

  if (!orderStore.eligibleForClaim) {
    requestError.value = t('alerts.orderNotProtected');

    return;
  }

  if (!orderStore.orderHasShipped) {
    navigateTo('/not-shipped');

    return;
  }

  if (orderStore.showTooLateAlert) {
    const days = currentOrder.fulfillments[0].maxClaimFilingWindow;

    requestWarning.value = t('alerts.maxClaimFilingWindowReached', { maxClaimFilingWindow: days });

    return;
  }

  if (orderStore.currentOrder && orderStore.currentOrder.settings?.capabilities?.returns && !claimStore.claimSession) {
    return returnRouter.validate();
  }

  if (isEmpty(currentOrder.existingClaims)) {
    if (orderStore.isOta) {
      showOtaModal.value = true;

      return;
    }

    claimRouter.validate();
  } else {
    navigateTo(`/process`);
  }
}

async function handleInStorePurchaseClick() {
  claimStore.setClaimCategory(ClaimCategory.WARRANTY);

  navigateTo('/in-store/contact-info');
}

async function handleContinueClick() {
  $v.value.$touch();

  if ($v.value.$invalid) {
    return;
  }

  requestError.value = null;
  loading.value = true;

  const cleanedOrderNumber = cleanOrderNumber(orderNumber.value);

  try {
    const result = await orderStore.find(emailAddress.value, cleanedOrderNumber);

    await handleFindOrderSuccess(result);
  } catch ({ response, ...error }) {
    console.log({ error });
    if (response?.data?.statusCode === 404) await findClaimInV1();
  } finally {
    loading.value = false;
  }
}

async function findClaimInV1() {
  const cleanedOrderNumber = cleanOrderNumber(orderNumber.value);

  const { data } = await useFetch('/api/check-claim-v1', {
    body: { email: emailAddress.value, storeOrderId: cleanedOrderNumber },
    method: 'POST',
  });
  if (data.value.hasV1Claim) {
    const uiClaimUrlV1 = useRuntimeConfig().public.v1.uiClaimUrl;
    const url = `${uiClaimUrlV1}/submitClaim?email=${emailAddress.value}&op_id=${cleanedOrderNumber}&fromEmail=true`;
    navigateTo(url, { external: true });
  } else {
    requestError.value = t('alerts.orderNotFound');
  }
}

async function getStore() {
  let store;

  if (whitelabel.storeId) {
    store = await merchantStore.find(whitelabel.storeId);
  } else if (slug.value.length > 0) {
    const response = await merchantStore.findBySlug(slug.value);
    store = response.data;
  }

  if (!store) return;

  inStore.setStore(store);
  inStoreEnabled.value = !!store?.warrantySettings?.inStoreWarranties;
}

onMounted(() => {
  getStore();
});
</script>

<i18n lang="json">
{
  "en": {
    "alerts": {
      "orderNotProtected": "No policy found on order. This order is not eligible for a claim.",
      "orderNotFound": "We couldn't find a match for that order number and email address. Please double-check your order confirmation email to ensure you're using the correct order number and email address for the order.",
      "fulfillmentsNotFound": "We couldn't find any eligible shipments for this order.",
      "maxClaimFilingWindowReached": "This claim submission exceeds the max claim filing window of { maxClaimFilingWindow } days from when this order was fulfilled."
    },
    "formLabels": {
      "emailAddress": "Email Address",
      "orderNumber": "Order Number"
    },
    "subtext": "It's an in-store purchase",
    "subTitle": "We're sorry to hear you have a problem with your order. Let's get it resolved.",
    "title": "Get a resolution for your order in minutes"
  }
}
</i18n>
